import { getFavorites, onFavoritesChange } from '@theme/favorites-store';

/**
 * <s-fav-list> renders the favorites page.
 *
 * For every saved handle it fetches the product URL with ?view=fav-card (a layout-less
 * product template), so price, stock, sizes and condition always come live from Shopify.
 * A product that no longer responds (deleted, unpublished, renamed without redirect)
 * stays in the list as a clearly marked "no longer available" card built from the
 * stored title/image, with a heart to remove it. Nothing is ever removed automatically.
 *
 * Children: [data-fav-loading], [data-fav-grid] (ul), [data-fav-empty], [data-fav-count],
 * [data-fav-status] (live region) and <template data-fav-unavailable>.
 * data-count-one / data-count-other ("__N__") hold the count copy.
 */
class SFavList extends HTMLElement {
  #ac = null;
  #renderId = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    onFavoritesChange((list) => this.#sync(list), this.#ac.signal);
    this.#render(getFavorites());
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #grid() {
    return this.querySelector('[data-fav-grid]');
  }

  async #render(list) {
    const renderId = ++this.#renderId;
    const grid = this.#grid;
    const loading = this.querySelector('[data-fav-loading]');
    if (!grid) return;

    if (list.length === 0) {
      this.#showEmpty();
      return;
    }

    if (loading) loading.hidden = false;
    const items = await Promise.all(list.map((item) => this.#buildItem(item)));
    // A newer render (e.g. a change in another tab) superseded this one.
    if (renderId !== this.#renderId) return;

    grid.replaceChildren(...items);
    if (loading) loading.hidden = true;
    this.#toggleEmpty(false);
    this.#updateCount(items.length);
  }

  #sync(list) {
    const grid = this.#grid;
    if (!grid) return;
    if (list.length === 0) {
      this.#renderId++;
      this.#showEmpty();
      return;
    }
    const handles = new Set(list.map((item) => item.handle));
    const shown = [...grid.children];
    const shownHandles = new Set(shown.map((li) => li.dataset.handle));
    const hasNew = list.some((item) => !shownHandles.has(item.handle));
    if (hasNew) {
      this.#render(list);
      return;
    }
    for (const li of shown) {
      if (!handles.has(li.dataset.handle)) li.remove();
    }
    this.#updateCount(grid.children.length);
  }

  async #buildItem(item) {
    const li = document.createElement('li');
    li.dataset.handle = item.handle;
    const card = await this.#fetchCard(item);
    li.append(card || this.#unavailableCard(item));
    return li;
  }

  async #fetchCard(item) {
    try {
      const url = new URL(item.url, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      url.searchParams.set('view', 'fav-card');
      const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: this.#ac.signal });
      if (!response.ok) return null;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const card = doc.querySelector('[data-fav-card]');
      return card ? document.adoptNode(card) : null;
    } catch {
      return null;
    }
  }

  #unavailableCard(item) {
    const template = this.querySelector('template[data-fav-unavailable]');
    const fragment = template.content.cloneNode(true);
    const root = fragment.firstElementChild;
    const title = root.querySelector('[data-fav-title]');
    if (title) title.textContent = item.title || item.handle;
    const image = root.querySelector('[data-fav-image]');
    if (image) {
      if (item.image) {
        image.src = item.image;
        image.alt = item.title || '';
      } else {
        image.remove();
      }
    }
    const button = root.querySelector('s-fav-button');
    if (button) {
      button.dataset.handle = item.handle;
      button.dataset.title = item.title || '';
      button.dataset.url = item.url || '';
      button.dataset.image = item.image || '';
    }
    return root;
  }

  #showEmpty() {
    const grid = this.#grid;
    const loading = this.querySelector('[data-fav-loading]');
    if (grid) grid.replaceChildren();
    if (loading) loading.hidden = true;
    this.#toggleEmpty(true);
    this.#updateCount(0);
  }

  #toggleEmpty(isEmpty) {
    const empty = this.querySelector('[data-fav-empty]');
    const grid = this.#grid;
    if (empty) empty.hidden = !isEmpty;
    if (grid) grid.hidden = isEmpty;
  }

  #updateCount(count) {
    const text = count === 1 ? this.dataset.countOne : (this.dataset.countOther || '').replace('__N__', String(count));
    const countEl = this.querySelector('[data-fav-count]');
    if (countEl) {
      countEl.hidden = count === 0;
      countEl.textContent = text || '';
    }
    const status = this.querySelector('[data-fav-status]');
    if (status) status.textContent = count === 0 ? this.dataset.emptyMessage || '' : text || '';
  }
}

if (!customElements.get('s-fav-list')) customElements.define('s-fav-list', SFavList);
